import $ from 'jquery';
import PropTypes from 'prop-types';
import React from 'react';

import InstitutionConstants, {Institution} from 'institutions/InstitutionConstants';
import {renderToStaticMarkup} from 'redux-stores/ReduxDOMRender';
import LegacySelect2, {LegacySelect2ChangeEvent, LegacySelect2Query} from 'ui-library/LegacySelect2';

export interface InstitutionSelectorProps {
  className?: string;
  name?: string;
  value?: Institution;
  onChange: (institution: Institution) => void;
  placeholder?: string;
  large?: boolean;
  allowUnverified?: boolean;
  allowUnverifiedSearch?: boolean;
  disabled?: boolean;
  allowLeaving?: boolean;
}

interface InstitutionOption {
  id: string;
  disabled?: boolean;
  text: string;
  institution?: Institution;
}

const TOO_MANY_RESULTS_OPTION: InstitutionOption = {
  id: '-1',
  disabled: true,
  text: 'Too many institutions found\u2014please refine your search.',
};

function institutionId(institution: Institution | undefined): string {
  return (institution && institution.id) || '';
}

function institutionOption(institution: Institution): InstitutionOption {
  const suffix = institution.licensed ? 'licensed' : institution.verified ? '' : 'unverified';
  const text = suffix ? `${institution.name} (${suffix})` : institution.name;
  return {
    text,
    id: institutionId(institution),
    institution,
  };
}

export function createUserSuppliedInstitutionOption(term: string): InstitutionOption {
  return institutionOption({
    id: `${InstitutionConstants.USER_SUPPLIED_ID_PREFIX}${term}`,
    name: term,
    fullName: term,
    shortName: '',
    names: [],
    subdomains: [],
    urlIdentifier: '',
    verified: false,
    licensed: false,
    hasUnlimitedSpace: false,
    grantsElnDesktopSync: false,
    adminEmail: '',
    enableExportNotifications: false,
    enableEmailChangeNotifications: false,
  });
}

// LegacySelect2 for institutions that exposes an input[name=institution]
export default function InstitutionSelector(props: InstitutionSelectorProps): JSX.Element {
  const institutionChangeHandler = (
    e: LegacySelect2ChangeEvent & {
      choice: {institution: Institution};
    }
  ): void => {
    props.onChange(e.choice.institution);
  };

  const createSearchChoice = (term: string, options: Array<InstitutionOption>): InstitutionOption | null => {
    if (!props.allowUnverified) {
      return null;
    }
    // Only create a manual institution if the term is non-empty and doesn't match an existing one.
    term = term.trim();
    const nameMatches = (name: string): boolean => name.toUpperCase() === term.toUpperCase();
    if (
      !term ||
      options.some((option) =>
        Boolean(
          option !== TOO_MANY_RESULTS_OPTION &&
            option.institution &&
            option.institution.names.some((name) => nameMatches(name))
        )
      )
    ) {
      return null;
    }
    return createUserSuppliedInstitutionOption(term);
  };

  return (
    <LegacySelect2
      name={props.name}
      large={props.large}
      placeholder={props.placeholder}
      className={props.className}
      allowClear={props.allowLeaving}
      disabled={false}
      disableInput={props.disabled}
      value={institutionId(props.value)}
      onChange={institutionChangeHandler}
      minimumInputLength={1}
      formatInputTooShort={() => 'Please enter 1 or more characters to search for your institution.'}
      initSelection={(elt, callback) => {
        if (props.value) {
          callback(institutionOption(props.value));
        }
      }}
      formatResult={function formatResult(
        result: InstitutionOption,
        container: JQuery,
        query: LegacySelect2Query,
        escapeMarkup: (markup: string) => string
      ): string {
        if (result === TOO_MANY_RESULTS_OPTION) {
          return TOO_MANY_RESULTS_OPTION.text;
        }
        const resultHTML = $.fn.select2.defaults.formatResult!(result, container, query, escapeMarkup);
        // eslint-disable-next-line react/no-danger
        return renderToStaticMarkup(<div dangerouslySetInnerHTML={{__html: resultHTML}} />);
      }}
      createSearchChoice={createSearchChoice}
      createSearchChoicePosition="bottom"
      ajax={{
        url: '/1/api/institutions/',
        dataType: 'json',
        data: (term: string) => ({q: term, allowUnverified: props.allowUnverifiedSearch}),
        results: ({results, more}: {results: Array<Institution>; more: boolean}) => {
          const formattedResults = results.map((institution) => institutionOption(institution));
          if (more) {
            formattedResults.push(TOO_MANY_RESULTS_OPTION);
          }
          return {results: formattedResults};
        },
      }}
    />
  );
}
InstitutionSelector.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: InstitutionConstants.PropTypes.institution,
  onChange: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  large: PropTypes.bool.isRequired,
  allowUnverified: PropTypes.bool.isRequired, // allows creating new unverified institutions
  allowUnverifiedSearch: PropTypes.bool.isRequired, // allows searching for unverified institutions
  disabled: PropTypes.bool,
  allowLeaving: PropTypes.bool,
};

InstitutionSelector.defaultProps = {
  name: 'institution',
  placeholder: 'Institution',
  onChange: (institution: Institution) => {},
  large: false,
  allowUnverified: true,
  allowUnverifiedSearch: false,
};
